import * as React from "react";
import { useTerms } from "@/hooks/useTerms";
import { Link } from "react-router-dom";
import {
  PageHeader,
  PageHeaderTop,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageHeaderActions,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatCard,
  DataTable,
  DataTableScroll,
  DataTableEmpty,
  DataTableSkeleton,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  cn,
} from "@eduviaos/ui-kit";
import { AlertCircle, CalendarClock, Download, RefreshCw, Users, UserMinus } from "lucide-react";
import type { WorkloadStaffStats } from "@eduviaos/shared";
import { useWorkload } from "@/hooks/useAdmissions";
import { useAuth } from "@/auth/AuthProvider";
import { AdmissionsSubNav } from "@/components/AdmissionsSubNav";
import { ExportCsvErrorBanner } from "@/components/ExportCsvErrorBanner";
import { useCsvExport } from "@/hooks/useCsvExport";
import type { CsvCellValue } from "@/lib/csv";
import {
  ADMISSIONS_WORKLOAD_CSV_COLUMNS,
  buildUnassignedWorkloadRow,
  mapWorkloadStaffToCsvRow,
} from "@/lib/admissionsCsv";

/**
 * AdmissionsWorkloadPage — Phase D5.
 *
 * "Who's swamped?" view for the Institution Admin (or anyone with
 * `manage_admissions`). Backed by `GET /admissions/workload` which
 * returns per-staff active counts, overdue follow-ups, and 0-7-day
 * windows, plus the global "unassigned active" gap.
 *
 * UX principles:
 *   - Stat strip up top mirrors dashboards: total active, overdue,
 *     unassigned, average per staff.
 *   - Sortable table by Active / Overdue / Today / Week. Default sort
 *     is "Overdue desc" so the most stressed staff member ranks first.
 *   - The "Unassigned" row is rendered as a pinned card above the
 *     table so it's never confused with a real staff member.
 *   - Iron Rule #16 — DataTable primitive, sticky header in scroll
 *     wrapper, right-aligned tabular numerics, no zebra stripes.
 */

type SortKey = "active" | "overdue" | "today" | "week" | "name";
type SortDir = "asc" | "desc";

export function AdmissionsWorkloadPage() {
  const { data, isLoading, isFetching, refetch, isError } = useWorkload();
  const { user } = useAuth();
  const { ct } = useTerms();
  const tenantSlug = user?.tenant_id ?? null;
  const [sortKey, setSortKey] = React.useState<SortKey>("overdue");
  const [sortDir, setSortDir] = React.useState<SortDir>("desc");

  const staff = React.useMemo(() => data?.staff ?? [], [data?.staff]);
  const unassigned = data?.unassigned_active ?? 0;
  const totalOverdue = data?.total_overdue ?? 0;
  const totalActive = staff.reduce((sum, s) => sum + s.active_enquiries, 0);

  const sorted = React.useMemo(() => {
    const out = [...staff];
    out.sort((a, b) => {
      const dir = sortDir === "desc" ? -1 : 1;
      switch (sortKey) {
        case "active":
          return dir * (a.active_enquiries - b.active_enquiries);
        case "overdue":
          return dir * (a.overdue_followups - b.overdue_followups);
        case "today":
          return dir * (a.due_today - b.due_today);
        case "week":
          return dir * (a.due_this_week - b.due_this_week);
        case "name": {
          const an = (a.user_name || a.user_email).toLowerCase();
          const bn = (b.user_name || b.user_email).toLowerCase();
          return dir * an.localeCompare(bn);
        }
      }
    });
    return out;
  }, [staff, sortKey, sortDir]);

  const onSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const avgPerStaff = staff.length > 0 ? Math.round(totalActive / staff.length) : 0;

  // D2.c / E1.a — the export is a pure FE composition of already-fetched
  // workload data. We delegate state machine, now-pinning, filename, blob
  // construction, and friendly error wrapping to <useCsvExport>, and only
  // describe HOW to map the workload rows into CSV cells here.
  const hasExportableRows = sorted.length > 0 || unassigned > 0;
  const csvExport = useCsvExport({
    filenamePrefix: "admissions-workload",
    tenantSlug,
    disabled: isLoading || !hasExportableRows,
    buildRows: () => {
      const staffRows: ReadonlyArray<ReadonlyArray<CsvCellValue>> = sorted.map((s) =>
        mapWorkloadStaffToCsvRow(s, totalActive),
      );
      const unassignedRow = buildUnassignedWorkloadRow(unassigned, totalActive);
      const rows: ReadonlyArray<ReadonlyArray<CsvCellValue>> = unassignedRow
        ? [...staffRows, unassignedRow]
        : staffRows;
      return { columns: ADMISSIONS_WORKLOAD_CSV_COLUMNS, rows };
    },
  });

  return (
    <>
      <AdmissionsSubNav />
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Workload</PageHeaderHeading>
            <PageHeaderDescription>
              Who owns what — at a glance. Spot bottlenecks and rebalance assignments.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={csvExport.handleExport}
              disabled={isLoading || csvExport.isExporting || !hasExportableRows}
              aria-label="Download workload as CSV"
              data-testid="admissions-workload-export-csv"
            >
              <Download className="size-4" aria-hidden="true" />
              {csvExport.isExporting ? "Preparing…" : "Download CSV"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh workload"
            >
              <RefreshCw
                className={cn("size-4", isFetching && "animate-spin")}
                aria-hidden="true"
              />
              Refresh
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <ExportCsvErrorBanner
        error={csvExport.exportError}
        onDismiss={csvExport.dismissError}
        variant="card"
        testid="admissions-workload-export-error"
        className="mt-6"
      />

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Users className="size-4" aria-hidden="true" />}
          label="Active enquiries"
          value={totalActive.toLocaleString()}
          caption={ct("Across all assigned staff")}
          tone="blue"
        />
        <StatCard
          icon={<AlertCircle className="size-4" aria-hidden="true" />}
          label="Overdue follow-ups"
          value={totalOverdue.toLocaleString()}
          caption={totalOverdue > 0 ? "Past their scheduled time" : "All on schedule"}
          tone={totalOverdue > 0 ? "amber" : "neutral"}
        />
        <StatCard
          icon={<UserMinus className="size-4" aria-hidden="true" />}
          label="Unassigned"
          value={unassigned.toLocaleString()}
          caption={unassigned > 0 ? "Need an owner" : "Every active enquiry has an owner"}
          tone={unassigned > 0 ? "amber" : "green"}
        />
        <StatCard
          icon={<CalendarClock className="size-4" aria-hidden="true" />}
          label={ct("Avg per staff")}
          value={avgPerStaff.toLocaleString()}
          caption={
            staff.length > 0
              ? ct(
                  `Across ${staff.length.toLocaleString()} active counsellor${staff.length === 1 ? "" : "s"}`,
                )
              : ct("No staff currently own enquiries")
          }
          tone="violet"
        />
      </div>

      {unassigned > 0 && (
        <Card className="mt-6 border-amber-500/30">
          <CardContent className="flex flex-wrap items-center gap-3 py-4">
            <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <UserMinus className="size-4" aria-hidden="true" />
            </span>
            <div className="flex-1">
              <p className="text-foreground text-sm font-semibold">
                {unassigned.toLocaleString()} unassigned enquir{unassigned === 1 ? "y" : "ies"}
              </p>
              <p className="text-muted-foreground text-xs">
                {ct(
                  "These are active in the funnel with no primary owner. Bulk-assign them to a counsellor so nothing falls through the cracks.",
                )}
              </p>
            </div>
            <Button asChild size="sm">
              <Link to="/admissions">Open enquiries list</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="mt-6">
        <CardHeader className="border-border/60 border-b pb-4">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Users className="text-muted-foreground size-4" aria-hidden="true" />
            {ct("Staff workload")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable>
            <DataTableScroll>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[36%]">
                      <SortHeader
                        label={ct("Staff member")}
                        active={sortKey === "name"}
                        dir={sortDir}
                        onClick={() => onSort("name")}
                      />
                    </TableHead>
                    <TableHead className="w-[16%] text-right">
                      <SortHeader
                        label="Active"
                        active={sortKey === "active"}
                        dir={sortDir}
                        onClick={() => onSort("active")}
                        align="right"
                      />
                    </TableHead>
                    <TableHead className="w-[16%] text-right">
                      <SortHeader
                        label="Overdue"
                        active={sortKey === "overdue"}
                        dir={sortDir}
                        onClick={() => onSort("overdue")}
                        align="right"
                      />
                    </TableHead>
                    <TableHead className="w-[16%] text-right">
                      <SortHeader
                        label="Today"
                        active={sortKey === "today"}
                        dir={sortDir}
                        onClick={() => onSort("today")}
                        align="right"
                      />
                    </TableHead>
                    <TableHead className="w-[16%] text-right">
                      <SortHeader
                        label="This week"
                        active={sortKey === "week"}
                        dir={sortDir}
                        onClick={() => onSort("week")}
                        align="right"
                      />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                {isLoading ? (
                  <DataTableSkeleton rows={6} columns={5} />
                ) : (
                  <TableBody>
                    {sorted.map((s) => (
                      <StaffRow key={s.user_id} staff={s} />
                    ))}
                  </TableBody>
                )}
              </Table>
            </DataTableScroll>

            {!isLoading && staff.length === 0 && !isError && (
              <DataTableEmpty
                icon={<Users aria-hidden="true" />}
                title="No assigned enquiries yet"
                description="Once enquiries get assigned, your team's workload will appear here."
                action={
                  <Button asChild size="sm">
                    <Link to="/admissions">Browse enquiries</Link>
                  </Button>
                }
              />
            )}

            {!isLoading && isError && (
              <DataTableEmpty
                icon={<AlertCircle aria-hidden="true" />}
                title="Couldn't load workload"
                description="Try refreshing — if it keeps happening, check your network."
                action={
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    <RefreshCw className="size-4" aria-hidden="true" />
                    Retry
                  </Button>
                }
              />
            )}
          </DataTable>
        </CardContent>
      </Card>
    </>
  );
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
  align = "left",
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: "left" | "right";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "hover:text-foreground inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider transition-colors",
        align === "right" && "ml-auto",
        active ? "text-foreground" : "text-muted-foreground",
      )}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
      {active && (
        <span className="text-[10px]" aria-hidden="true">
          {dir === "asc" ? "▲" : "▼"}
        </span>
      )}
    </button>
  );
}

function StaffRow({ staff }: { staff: WorkloadStaffStats }) {
  const overdueTone =
    staff.overdue_followups >= 5
      ? "text-rose-600 dark:text-rose-400"
      : staff.overdue_followups > 0
        ? "text-amber-600 dark:text-amber-400"
        : "text-muted-foreground";
  const todayTone = staff.due_today > 0 ? "text-foreground font-semibold" : "text-muted-foreground";
  const initials =
    (staff.user_name || staff.user_email)
      .split(/[\s.@_-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "?";

  return (
    <TableRow data-testid={`workload-row-${staff.user_id}`}>
      <TableCell>
        <Link
          to={`/admissions?assigned_to=${encodeURIComponent(staff.user_id)}`}
          className="hover:text-primary inline-flex items-center gap-2.5 transition-colors"
        >
          <span
            aria-hidden="true"
            className="bg-primary/15 text-primary inline-flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
          >
            {initials}
          </span>
          <span className="min-w-0">
            <span className="text-foreground block truncate text-sm font-medium">
              {staff.user_name || staff.user_email}
            </span>
            {staff.user_name && (
              <span className="text-muted-foreground block truncate text-[11px]">
                {staff.user_email}
              </span>
            )}
          </span>
        </Link>
      </TableCell>
      <TableCell className="text-foreground text-right text-sm tabular-nums">
        {staff.active_enquiries.toLocaleString()}
      </TableCell>
      <TableCell className={cn("text-right text-sm tabular-nums", overdueTone)}>
        {staff.overdue_followups.toLocaleString()}
      </TableCell>
      <TableCell className={cn("text-right text-sm tabular-nums", todayTone)}>
        {staff.due_today.toLocaleString()}
      </TableCell>
      <TableCell className="text-muted-foreground text-right text-sm tabular-nums">
        {staff.due_this_week.toLocaleString()}
      </TableCell>
    </TableRow>
  );
}
